import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';

// Import types
import { LocationConfig } from './types/AccuLynxTypes';

// Import custom hooks
import { useAccuLynxData } from './hooks/useAccuLynxData';
import { useAccuLynxActions } from './hooks/useAccuLynxActions';

// Import components
import { LocationCard } from './LocationCard';
// import { HelpPanel } from './HelpPanel';
import { LoadingSpinner } from './LoadingSpinner';
import { HeaderSection } from './HeaderSection';
import { GetStartedSection } from './GetStartedSection';
import { ConfirmationModal } from './ConfirmationModal';

// Import styled components
import { StyledForm, Section } from './styles/AccuLynxStyles';
import { ACCULYNX_CONFIG_API_BASE } from 'application/config/apiConfig';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

export const AccuLynxSetup: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();

  // Custom hooks for data and actions
  const {
    isLoading,
    isLoadingCircles,
    isLoadingStages,
    circles,
    paivStages,
    config,
    isEditing,
    savedConfig,
    setConfig,
    setIsEditing,
    setSavedConfig,
    stageCategories,
    circleLevels,
    hideCrmCategory,
    hideLocationFields,
  } = useAccuLynxData();

  const hasIntegration = savedConfig.locations.length > 0;

  const {
    isSubmitting,
    submitStatus,
    hasChanges,
    addLocation,
    removeLocation,
    updateLocation,
    handleApiKeyChange,
    updateStageMapping,
    validateConfig,
    handleSubmit,
    handleCancel,
    handleGetStarted,
    setSubmitStatus,
  } = useAccuLynxActions(
    config,
    setConfig,
    paivStages,
    circles,
    isEditing,
    savedConfig,
    setIsEditing,
    setSavedConfig,
    stageCategories
  );

  // If no saved integration exists (after loading completes), auto-enable edit mode
  useEffect(() => {
    if (!isLoading && !isEditing && savedConfig.locations.length === 0) {
      setIsEditing(true);
    }
  }, [isLoading, isEditing, savedConfig.locations.length, setIsEditing]);

  // Turn-off flow state
  const [isTurningOff, setIsTurningOff] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText?: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: () => {},
  });

  // Turn off AccuLynx for this org by sending empty locations
  const turnOffIntegration = async (): Promise<void> => {
    setConfirmationModal({
      open: true,
      title: 'Turn Off AccuLynx',
      message:
        'Are you sure you want to turn off AccuLynx for this organization?\n\nThis will:\n• Remove all AccuLynx integrations\n• Unsubscribe from all webhooks\n• Delete all stage mappings\n• This action cannot be undone',
      confirmText: 'Turn Off',
      onConfirm: async () => {
        setConfirmationModal((prev) => ({ ...prev, open: false }));
        await performTurnOff();
      },
    });
  };

  // Actually perform the turn off operation
  const performTurnOff = async (): Promise<void> => {
    try {
      setIsTurningOff(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const authHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      // Delete each saved config individually
      const errors: string[] = [];
      for (const location of savedConfig.locations) {
        const configId = (location as any)?.id;
        if (!configId) continue;

        try {
          const res = await fetch(`${ACCULYNX_CONFIG_API_BASE}/${configId}`, {
            method: 'DELETE',
            headers: authHeaders,
          });
          const result = await res.json();
          if (!res.ok || !result?.success) {
            errors.push(result?.error || `Failed to delete ${configId}`);
          }
        } catch (e: any) {
          errors.push(e?.message || `Failed to delete ${configId}`);
        }
      }

      if (errors.length > 0) {
        throw new Error(errors[0]);
      }

      // Clear local state completely
      setConfig({ locations: [], stageMappings: [] });
      setSavedConfig({ locations: [], stageMappings: [] });
      setIsEditing(false);
      setSubmitStatus({
        type: 'success',
        message: 'AccuLynx turned off',
      });
    } catch (e: any) {
      setSubmitStatus({
        type: 'error',
        message: e?.message || 'Failed to turn off AccuLynx',
      });
    } finally {
      setIsTurningOff(false);
    }
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <HeaderSection
            isEditing={isEditing}
            hasIntegration={hasIntegration}
            isSubmitting={isSubmitting}
            validateConfig={validateConfig}
            onSubmit={handleSubmit}
            onEdit={() =>
              setConfirmationModal({
                open: true,
                title: 'Edit Configuration',
                message:
                  'Are you sure you want to edit this integration?\n\nYou will be able to change locations, API keys, and stage mappings.',
                confirmText: 'Edit',
                onConfirm: () => {
                  setConfirmationModal((prev) => ({ ...prev, open: false }));
                  setIsEditing(true);
                },
              })
            }
            onCancel={handleCancel}
          />

          {submitStatus.type && (
            <Alert
              severity={submitStatus.type}
              sx={{ mb: 3 }}
              onClose={() => setSubmitStatus({ type: undefined, message: '' })}
            >
              {submitStatus.message}
            </Alert>
          )}

          {!hasIntegration && !isEditing ? (
            <GetStartedSection onGetStarted={handleGetStarted} />
          ) : (
            <StyledForm onSubmit={handleSubmit}>
              <Section>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">Locations</Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<HelpOutlineIcon />}
                      onClick={() => setHelpDialogOpen(true)}
                    >
                      Help
                    </Button>
                  </Box>
                </Box>

                {/* Render location cards */}
                <Box sx={{ position: 'relative', minHeight: 200 }}>
                  {isLoadingStages && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        zIndex: 5,
                        borderRadius: 1,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  {config.locations.map(
                    (location: LocationConfig, locationIndex: number) => (
                      <LocationCard
                        key={locationIndex}
                        location={location}
                        locationIndex={locationIndex}
                        isEditing={isEditing}
                        isLoadingCircles={isLoadingCircles}
                        isLoadingStages={isLoadingStages}
                        circles={circles}
                        paivStages={paivStages}
                        stageCategories={stageCategories}
                        circleLevels={circleLevels}
                        hideCrmCategory={hideCrmCategory}
                        hideLocationFields={hideLocationFields}
                        onUpdateLocation={updateLocation}
                        onApiKeyChange={handleApiKeyChange}
                        onRemoveLocation={removeLocation}
                        onUpdateStageMapping={updateStageMapping}
                      />
                    )
                  )}
                </Box>

                {isEditing && !hideLocationFields && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={addLocation}
                      startIcon={<AddIcon />}
                    >
                      Add Location
                    </Button>
                  </Box>
                )}
              </Section>

              {isEditing && hasIntegration && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    onClick={turnOffIntegration}
                    disabled={isTurningOff}
                  >
                    {isTurningOff ? 'Turning off...' : 'Turn off AccuLynx'}
                  </Button>
                </Box>
              )}

              {/* Save/Cancel Footer */}
              {isEditing && (
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    zIndex: 10,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/settings/integrations')}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !validateConfig() || !hasChanges}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Integration'}
                  </Button>
                </Box>
              )}
            </StyledForm>
          )}

          {/* Removed top-right Help button */}

          {/* Confirmation Modal */}
          <ConfirmationModal
            open={confirmationModal.open}
            title={confirmationModal.title}
            message={confirmationModal.message}
            onConfirm={confirmationModal.onConfirm}
            onCancel={() =>
              setConfirmationModal((prev) => ({ ...prev, open: false }))
            }
            confirmText={confirmationModal.confirmText || 'Confirm'}
            cancelText="Cancel"
            severity="warning"
          />

          {/* HelpPanel removed as per edit hint */}

          {/* Help Dialog */}
          <Dialog
            open={helpDialogOpen}
            onClose={() => setHelpDialogOpen(false)}
            maxWidth="md"
            fullWidth
            scroll="paper"
            sx={{ '& .MuiDialog-paper': { maxHeight: '70vh' } }}
          >
            <DialogTitle>AccuLynx Integration Help</DialogTitle>
            <DialogContent dividers>
              <Typography variant="h6" sx={{ mb: 2 }}>
                1. Connect AccuLynx
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Enter your AccuLynx API key on the Paiv AccuLynx setup page." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Choose how you want to map your AccuLynx stages to Paiv stages. You can choose to map your AccuLynx locations by Company, Division, or Team." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Save the integration to begin syncing." />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                2. Stage Mapping Guidance
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Map each AccuLynx stage to a corresponding Paiv stage for consistent syncing." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="You can edit mappings anytime; changes take effect on the next sync." />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Troubleshooting
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Authentication Errors"
                    secondary="Verify your credentials and permissions in AccuLynx."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Missing Stages"
                    secondary="Refresh stages and ensure your Paiv account has access to the pipeline you expect to sync."
                  />
                </ListItem>
              </List>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Need assistance? Contact{' '}
                <Link href="mailto:support@paiv.ai">support@paiv.ai</Link>.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setHelpDialogOpen(false)}
                variant="contained"
              >
                Got it
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};
